// Light Ruby syntax colouring for the browser Rails console: the code typed
// in and the `=> result.inspect` that comes back. Like logSyntax.js it splits
// text into [{ text, cls }] segments that join back into the exact input.
//
// Results are inspect output (#<User id: 1 ...>), where # starts an object,
// not a comment, so tokenize(text, { comments: false }) for those.

const C = {
  plain: 'text-gray-100',
  keyword: 'text-fuchsia-400',
  literal: 'text-violet-300',
  string: 'text-emerald-300',
  symbol: 'text-cyan-300',
  number: 'text-orange-300',
  constant: 'text-sky-300',
  ivar: 'text-rose-300',
  comment: 'text-gray-500 italic',
  arrow: 'text-gray-500',
}

const KEYWORDS = 'def|end|do|if|else|elsif|unless|while|until|for|in|return|class|module|self|and|or|not|then|yield|begin|rescue|ensure|case|when|lambda|proc|next|break|super|require|raise'

// Order matters: comments and strings first, so a # or keyword inside a
// string stays part of the string.
const PARTS = [
  ['comment', /#[^\n]*/.source],
  ['string', /"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/.source],
  ['symbol', /(?<![:\w]):[A-Za-z_]\w*[?!=]?|\b[a-z_]\w*:(?!:)/.source],
  ['ivar', /@@?\w+|\$\w+/.source],
  ['number', /\b\d[\d_]*(?:\.\d+)?\b/.source],
  ['literal', /\b(?:nil|true|false)\b/.source],
  ['keyword', `\\b(?:${KEYWORDS})\\b`],
  ['constant', /\b[A-Z]\w*/.source],
]

function pattern(comments) {
  const parts = comments ? PARTS : PARTS.filter(([name]) => name !== 'comment')
  return new RegExp(parts.map(([name, source]) => `(?<${name}>${source})`).join('|'), 'g')
}

const WITH_COMMENTS = pattern(true)
const WITHOUT_COMMENTS = pattern(false)

export function tokenize(text, { comments = true } = {}) {
  if (!text) return []
  const re = comments ? WITH_COMMENTS : WITHOUT_COMMENTS
  const out = []
  let last = 0
  re.lastIndex = 0
  let start = 0
  if (!comments && text.startsWith('=> ')) {
    out.push({ text: '=> ', cls: C.arrow })
    start = last = 3
  }
  for (const m of text.slice(start).matchAll(re)) {
    const index = m.index + start
    if (index > last) out.push({ text: text.slice(last, index), cls: C.plain })
    const kind = Object.keys(m.groups).find((name) => m.groups[name] !== undefined)
    out.push({ text: m[0], cls: C[kind] })
    last = index + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last), cls: C.plain })
  return out
}

// JSON view of a result: keys cyan, strings green, numbers orange, true /
// false / null violet, [FILTERED] dimmed. Lossless like tokenize().
const JSON_PARTS = /(?<key>"(?:[^"\\]|\\.)*"(?=\s*:))|(?<string>"(?:[^"\\]|\\.)*")|(?<number>-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b)|(?<literal>\b(?:true|false|null)\b)/g

export function tokenizeJson(text) {
  const out = []
  let last = 0
  for (const m of text.matchAll(JSON_PARTS)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), cls: 'text-gray-500' })
    const kind = Object.keys(m.groups).find((name) => m.groups[name] !== undefined)
    const cls = m[0] === '"[FILTERED]"' ? 'text-gray-500 italic' : { key: C.symbol, string: C.string, number: C.number, literal: C.literal }[kind]
    out.push({ text: m[0], cls })
    last = m.index + m[0].length
  }
  if (last < text.length) out.push({ text: text.slice(last), cls: 'text-gray-500' })
  return out
}
